import CheckboxTreeNodeDto from "../../types/dtos/generic/CheckboxTreeNodeDto";
import {
  checkboxTreeLabelMap,
  compressCheckboxTreeSelection,
} from "../searchableFilterChecklist";

export type AudienceFacetType =
  | "USER-GROUP"
  | "JOB-TITLE"
  | "APPRAISAL-LEVEL"
  | "LOCATION";

type TranslateFn = (
  key: string,
  fallback: string,
  options?: Record<string, unknown>
) => string;

export function audienceFacetLabel(
  type: AudienceFacetType,
  t: (key: string, fallback: string) => string
): string {
  switch (type) {
    case "JOB-TITLE":
      return t("Analytics.Filters.JobTitles", "Job titles");
    case "APPRAISAL-LEVEL":
      return t("Analytics.Filters.JobLevels", "Job levels");
    case "LOCATION":
      return t("Analytics.Filters.Locations", "Locations");
    case "USER-GROUP":
      return t("Analytics.Filters.UserGroups", "User groups");
  }
}

/** Count-only fallback (no catalogue / tree). */
export function audienceFacetSummary(
  type: AudienceFacetType,
  count: number,
  t: TranslateFn
): string {
  if (count <= 0) {
    return t("Analytics.Filters.All", "All");
  }
  switch (type) {
    case "JOB-TITLE":
      return count === 1
        ? t("Analytics.Filters.NJobTitle", "1 job title")
        : t("Analytics.Filters.NJobTitles", "{{count}} job titles", { count });
    case "APPRAISAL-LEVEL":
      return count === 1
        ? t("Analytics.Filters.NJobLevel", "1 job level")
        : t("Analytics.Filters.NJobLevels", "{{count}} job levels", { count });
    case "LOCATION":
      return count === 1
        ? t("Analytics.Filters.NLocation", "1 location")
        : t("Analytics.Filters.NLocations", "{{count}} locations", { count });
    case "USER-GROUP":
      return count === 1
        ? t("Analytics.Filters.NUserGroup", "1 user group")
        : t("Analytics.Filters.NUserGroups", "{{count}} user groups", {
            count,
          });
  }
}

export interface FormatAudienceFacetSummaryArgs {
  type: AudienceFacetType;
  selectedIds: number[] | null | undefined;
  t: TranslateFn;
  /** Flat label lookup for job titles / levels / groups (and location fallback). */
  labelById?: Map<number, string>;
  /** Full location tree for effective-root compression. */
  locationTree?: CheckboxTreeNodeDto[];
}

/**
 * Chip / footer summary: one effective selection → name; many → count.
 * Locations compress to tree roots when `locationTree` is available.
 */
export function formatAudienceFacetSummary({
  type,
  selectedIds,
  t,
  labelById,
  locationTree,
}: FormatAudienceFacetSummaryArgs): string {
  const ids = selectedIds ?? [];
  if (ids.length === 0) {
    return t("Analytics.Filters.All", "All");
  }

  if (type === "LOCATION" && locationTree?.length) {
    const roots = compressCheckboxTreeSelection(locationTree, ids);
    if (roots.length === 1) {
      return roots[0].label;
    }
    if (roots.length > 1) {
      return audienceFacetSummary(type, roots.length, t);
    }
    // Selected ids not found in tree — fall through to raw count / labels
  }

  if (ids.length === 1) {
    const name = labelById?.get(ids[0]);
    if (name) return name;
  }

  return audienceFacetSummary(type, ids.length, t);
}

/** Effective selection count for badges (locations: compressed roots). */
export function audienceFacetEffectiveCount(
  type: AudienceFacetType,
  selectedIds: number[] | null | undefined,
  locationTree?: CheckboxTreeNodeDto[]
): number {
  const ids = selectedIds ?? [];
  if (ids.length === 0) return 0;
  if (type === "LOCATION" && locationTree?.length) {
    const roots = compressCheckboxTreeSelection(locationTree, ids);
    return roots.length > 0 ? roots.length : ids.length;
  }
  return ids.length;
}

export function labelMapFromCheckboxTree(
  nodes: CheckboxTreeNodeDto[] | undefined
): Map<number, string> {
  return checkboxTreeLabelMap(nodes);
}

/** Picker submit: empty → no restriction; otherwise the checked ids (including “all”). */
export function submitAudienceFacetPickerIds(checked: string[]): number[] {
  if (checked.length === 0) return [];
  return checked
    .map((x) => parseInt(x, 10))
    .filter((n) => !Number.isNaN(n));
}
